import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Bell,
  Search,
  Settings,
  HelpCircle,
  ChevronDown,
  Shield,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface DashboardHeaderProps {
  onNotificationClick?: () => void;
  onHelpClick?: () => void;
  onSettingsClick?: () => void;
  onProfileClick?: () => void;
  onExportClick?: () => void;
  onScheduleClick?: () => void;
  onSearchChange?: (query: string) => void;
}

export function DashboardHeader({
  onNotificationClick,
  onHelpClick,
  onSettingsClick,
  onProfileClick,
  onExportClick,
  onScheduleClick,
  onSearchChange
}: DashboardHeaderProps) {
  const { user, organization } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [alertCount, setAlertCount] = useState(0);

  // Initialize alert count from last known value for this user
  useEffect(() => {
    if (user?.id) {
      const stored = localStorage.getItem(`infervia_last_unread_count_${user.id}`);
      if (stored !== null) {
        try {
          setAlertCount(JSON.parse(stored));
        } catch {
          setAlertCount(0);
        }
      } else {
        setAlertCount(0);
      }
    } else {
      setAlertCount(0);
    }
  }, [user?.id]);

  // Keep badge in sync when alerts update elsewhere (e.g., AlertsPanel mark read)
  useEffect(() => {
    const handleAlertsUpdated = (event: Event) => {
      const detail = (event as CustomEvent)?.detail;
      if (detail?.userId && user?.id && detail.userId !== user.id) return;
      if (typeof detail?.unreadCount === 'number') {
        setAlertCount(detail.unreadCount);
      }
    };

    window.addEventListener('alerts:updated', handleAlertsUpdated);
    return () => window.removeEventListener('alerts:updated', handleAlertsUpdated);
  }, [user?.id]);

  // Debounced search - only trigger search after user stops typing
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(searchQuery);
      }
    }, 300); // 300ms delay

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery, onSearchChange]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const email = user.email;
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Format role for display
  const formatRole = (role: string) => {
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <header className="bg-card border-b border-border px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Organization Info */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-sm font-semibold">{organization?.name || 'Infervia'}</h1>
              <p className="text-xs text-muted-foreground">Healthcare Regulatory Intelligence</p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="hidden md:flex items-center space-x-4 flex-1 max-w-md mx-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search policies, alerts, or compliance topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* User Controls */}
        <div className="flex items-center space-x-4">
          {/* Notifications - CLICKABLE */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="relative"
            onClick={onNotificationClick}
          >
            <Bell className="w-4 h-4" />
            {alertCount > 0 && (
              <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 bg-red-500 text-white text-xs flex items-center justify-center">
                {alertCount > 99 ? '99+' : alertCount}
              </Badge>
            )}
          </Button>

          {/* Help - CLICKABLE */}
          <Button variant="ghost" size="sm" onClick={onHelpClick}>
            <HelpCircle className="w-4 h-4" />
          </Button>

          {/* Settings - CLICKABLE */}
          <Button variant="ghost" size="sm" onClick={onSettingsClick}>
            <Settings className="w-4 h-4" />
          </Button>

          {/* User Profile - CLICKABLE */}
          <div className="flex items-center space-x-3 pl-4 border-l border-border">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.email || 'User'}</p>
              <p className="text-xs text-muted-foreground">
                {user?.role ? formatRole(user.role) : 'Staff'}
                {user?.department && ` • ${user.department}`}
              </p>
            </div>
            <Button variant="ghost" className="p-0 h-auto" onClick={onProfileClick}>
              <div className="flex items-center space-x-2">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </div>
            </Button>
          </div>
        </div>
      </div>

      {/* Secondary Navigation */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Last sync:</span>
            <span className="text-sm font-medium">2 minutes ago</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-green-700 font-medium">System healthy</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button variant="outline" size="sm" onClick={onExportClick}>
            Export Report
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={onScheduleClick}>
            Schedule Review
          </Button>
        </div>
      </div>
    </header>
  );
}
